import type { VisualEffect } from '@/types/effects';
import { VideoTemplate } from "@/types/template";
import { generateAllTemplates } from './template-generator';
import { TEMPLATE_LAYOUTS } from './layouts';
import { TEMPLATE_THEMES } from './themes';
import type { TemplateOptions } from '../types';

const AUTO_GENERATED_TEMPLATES = generateAllTemplates();

const LEGACY_TEMPLATES: VideoTemplate[] = [
    {
        id: 'basic',
        name: 'Basic',
        description: '기본 스타일 (전체 화면)',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            width: 1080,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            captionY: 0.8
        }
    },
    {
        id: 'basic-8bit-black',
        name: '8bit Black',
        description: '8비트 느낌 캡션 (배경 없음, 검정 외곽선)',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Jua',
            fontSize: 60,
            width: 1080,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.8,
            stroke: {
                width: 6,
                color: '#000000',
            },
        }
    },
    {
        id: 'basic-8bit-white',
        name: '8bit White',
        description: '8비트 느낌 캡션 (배경 없음, 흰 외곽선)',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Jua',
            fontSize: 60,
            width: 1080,
            color: '#000000',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.8,
            stroke: {
                width: 5,
                color: '#ffffff',
            },
        }
    },
    {
        id: 'basic-8bit-navy',
        name: '8bit Navy',
        description: '8비트 느낌 캡션 (배경 없음, 남색 외곽선)',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Jua',
            fontSize: 60,
            width: 1080,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.8,
            stroke: {
                width: 5,
                color: '#1e3a8a',
            },
        }
    },
    {
        id: 'basic-8bit-amber',
        name: '8bit Amber',
        description: '8비트 느낌 캡션 (배경 없음, 앰버 외곽선)',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Jua',
            fontSize: 60,
            width: 1080,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.8,
            stroke: {
                width: 5,
                color: '#f59e0b',
            },
        }
    },
    {
        id: 'basic-8bit-pink',
        name: '8bit Pink',
        description: '8비트 느낌 캡션 (배경 없음, 핑크 외곽선)',
        layoutId: 'basic',
        themeId: 'modern_blue',
        version: '1.0.0',
        layout: {
            x: 0,
            y: 0,
            width: 1080,
            height: 1920,
            scale: 1,
            objectFit: 'contain',
        },
        textStyle: {
            fontFamily: 'Jua',
            fontSize: 60,
            width: 1080,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            captionY: 0.8,
            stroke: {
                width: 5,
                color: '#ec4899',
            },
        }
    }
];

const RAW_TEMPLATES: VideoTemplate[] = [
    ...LEGACY_TEMPLATES,
    ...AUTO_GENERATED_TEMPLATES.filter(t =>
        !LEGACY_TEMPLATES.some(legacy => legacy.id === t.id)
    )
];

export const DEFAULT_TEMPLATE_VIGNETTE: VisualEffect = {
    type: 'overlay-effect',
    variant: 'vignette',
    intensity: 0.52,
    size: 0.62,
    softness: 0.68,
    color: '#000000',
    shape: 'elliptical',
};

const BASIC_TEMPLATES = RAW_TEMPLATES.filter((template) => template.layoutId === 'basic');
const fallbackTemplate = BASIC_TEMPLATES[BASIC_TEMPLATES.length - 1] || RAW_TEMPLATES[RAW_TEMPLATES.length - 1] || LEGACY_TEMPLATES[0];

export const DEFAULT_TEMPLATE_ID = fallbackTemplate?.id || 'basic';

export const VIDEO_TEMPLATES: VideoTemplate[] = RAW_TEMPLATES.map((template) => {
    if (template.id !== DEFAULT_TEMPLATE_ID) {
        return template;
    }

    return {
        ...template,
        visualEffects: [
            ...(template.visualEffects || []),
            DEFAULT_TEMPLATE_VIGNETTE,
        ],
    };
});

export { TEMPLATE_LAYOUTS, TEMPLATE_THEMES };
export { getCompatibleThemes, getCompatibleLayouts } from './template-generator';

export function isDefaultTemplateVignetteEffect(effect: VisualEffect | undefined): boolean {
    return (
        effect?.type === 'overlay-effect' &&
        effect.variant === 'vignette' &&
        effect.intensity === DEFAULT_TEMPLATE_VIGNETTE.intensity &&
        effect.size === DEFAULT_TEMPLATE_VIGNETTE.size &&
        effect.softness === DEFAULT_TEMPLATE_VIGNETTE.softness &&
        effect.color === DEFAULT_TEMPLATE_VIGNETTE.color &&
        effect.shape === DEFAULT_TEMPLATE_VIGNETTE.shape
    );
}

export function getTemplateVisualEffects(
    template: VideoTemplate,
    templateOptions?: Partial<TemplateOptions>
): VisualEffect[] | undefined {
    if (template.id !== DEFAULT_TEMPLATE_ID || templateOptions?.vignetteEnabled !== false) {
        return template.visualEffects;
    }

    const filteredEffects = (template.visualEffects || []).filter(
        (effect) => !isDefaultTemplateVignetteEffect(effect)
    );

    return filteredEffects.length > 0 ? filteredEffects : undefined;
}

export function templatePreviewHasVignette(
    template: VideoTemplate,
    templateOptions?: Partial<TemplateOptions>
): boolean {
    return (getTemplateVisualEffects(template, templateOptions) || []).some(
        (effect) => effect.type === 'overlay-effect' && effect.variant === 'vignette'
    );
}

export function getTemplateById(templateId: string): VideoTemplate | undefined {
    return VIDEO_TEMPLATES.find(t => t.id === templateId);
}
